import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SmoothMarkdown } from '@/components/SmoothMarkdown';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';

interface PreferenceSectionProps {
  autoFocus?: boolean;
  content: string;
  editDisabled: boolean;
  editValue: string;
  isEditing: boolean;
  isRegenerating: boolean;
  isSaving: boolean;
  label: string;
  onCancel: () => void;
  onEditValueChange: (value: string) => void;
  onSave: () => void;
  onStartEdit: () => void;
  placeholder: string;
  saveDisabled: boolean;
}

export function PreferenceSection({
  autoFocus = false,
  content,
  editDisabled,
  editValue,
  isEditing,
  isRegenerating,
  isSaving,
  label,
  onCancel,
  onEditValueChange,
  onSave,
  onStartEdit,
  placeholder,
  saveDisabled,
}: PreferenceSectionProps) {
  const { t } = useTranslation();

  return (
    // The height floor (24px label row + 6px gap + 120px card) is deliberately not
    // overridable: these sections are flex items in a scrolling column, so a zero
    // floor lets one shrink below the card it contains and the card then paints over
    // the next section's label. With a floor the parent scrolls instead.
    <div className="flex min-h-[150px] flex-1 flex-col gap-1.5 transition-all duration-300 ease-out">
      <div className="flex items-center justify-between">
        <label className="text-foreground font-['Inter'] text-[12px] leading-[170%] font-medium tracking-normal">
          {label}
        </label>
        {isEditing ? (
          <div className="flex items-center gap-1">
            <Button
              className="text-primary-active border-border-subtle hover:bg-back-bg bg-card h-6 rounded-md border px-2 text-[11px] font-medium"
              disabled={saveDisabled}
              onClick={onSave}
              type="button"
              variant="outline"
            >
              {isSaving ? (
                <Spinner className="text-primary-active size-3" />
              ) : (
                t('profile.draft.saveProfile')
              )}
            </Button>
            <Button
              className="border-border-subtle text-tertiary hover:bg-back-bg2 bg-card h-6 rounded-md border px-2 text-[11px] font-medium"
              disabled={isSaving}
              onClick={onCancel}
              type="button"
              variant="outline"
            >
              {t('profile.draft.cancelEdit')}
            </Button>
          </div>
        ) : (
          <Button
            className="h-6 w-6 rounded-full p-0"
            disabled={editDisabled}
            onClick={onStartEdit}
            type="button"
            variant="ghost"
          >
            <Pencil className="text-foreground h-4 w-4" />
          </Button>
        )}
      </div>
      {isEditing ? (
        <Textarea
          autoFocus={autoFocus}
          className="custom-scrollbar border-border-subtle bg-back-bg text-secondary focus:border-border-subtle h-full min-h-[120px] resize-y overflow-y-auto rounded-sm border-[0.5px] p-2 font-['Inter'] text-[12px] leading-[170%] font-normal tracking-normal transition-all duration-300 ease-out outline-none focus:shadow-none focus:ring-0 focus:outline-none focus-visible:ring-0"
          onChange={(e) => onEditValueChange(e.target.value)}
          value={editValue}
        />
      ) : (
        <div className="border-border-subtle bg-card relative h-full min-h-[120px] overflow-hidden rounded-sm border-[0.5px] p-2 transition-all duration-300 ease-out">
          <div
            className={`custom-scrollbar h-full overflow-y-auto transition-all duration-500 ease-out ${
              isRegenerating ? 'opacity-45 saturate-50' : 'opacity-100 saturate-100'
            }`}
          >
            {content ? (
              <div className="text-neutral-mid [&_blockquote]:border-border-subtle [&_h1]:text-tertiary [&_h2]:text-tertiary [&_h3]:text-tertiary [&_strong]:text-tertiary font-['Inter'] text-[12px] leading-[170%] font-normal tracking-normal [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:pl-2 [&_em]:italic [&_h1]:mt-2 [&_h1]:mb-1.5 [&_h1]:text-[13px] [&_h1]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1.5 [&_h2]:text-[12px] [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&_h3]:text-[12px] [&_h3]:font-medium [&_li]:mb-0.5 [&_ol]:mt-1 [&_ol]:mb-1.5 [&_ol]:list-decimal [&_ol]:pl-4 [&_p]:mb-1.5 [&_strong]:font-semibold [&_ul]:mt-1 [&_ul]:mb-1.5 [&_ul]:list-disc [&_ul]:pl-4">
                <SmoothMarkdown content={content} />
              </div>
            ) : (
              <p className="text-neutral-mid font-['Inter'] text-[12px] leading-[170%] font-normal tracking-normal">
                {placeholder}
              </p>
            )}
          </div>
          {isRegenerating ? (
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="profile-preference-shimmer via-card/70 absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-transparent to-transparent" />
              <div className="bg-card/25 absolute inset-0 backdrop-blur-[1px]" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
